"use client";

import type { ReactNode } from "react";
import { Box } from "@chakra-ui/react";
import { MindstringPrototype, type Situation } from "@/generated/fastapi";
import { useUserChoices } from "@/state/globalStore";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping/config/actions";
import { useSystemDesign } from "@/shared/system-design";
import type { ContentItemAction } from "@/shared/ui/display/ContentItemPrimitives";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import { useStudioHandlersContext } from "../contexts/StudioHandlersProvider";
import { useStudioIndexHits } from "../contexts/StudioIndexHitsContext";
import SituationHeaderItem from "../components/SituationHeaderItem";

type StudioSituationSectionProps = {
  isLocked: boolean;
  isManualAddGuidanceActive: boolean;
  prototype: MindstringPrototype;
  situation: Situation;
  situationContentComponent: (props: {
    situationId: string;
    prototype: MindstringPrototype;
  }) => ReactNode;
};

export default function StudioSituationSection({
  isLocked,
  isManualAddGuidanceActive,
  prototype,
  situation,
  situationContentComponent,
}: StudioSituationSectionProps) {
  const { isMobileDevice } = useSystemDesign();
  const {
    setInterimState,
    resetInterimState,
    isSelectedSituation,
    getSelectedThoughts,
    countSelectedThoughts,
    setActivePrototypeCategory,
  } = useUserChoices();
  const { situationHandlers } = useStudioHandlersContext();
  const { getIndexHits } = useStudioIndexHits();

  const situationId = situation.id;
  const clearProfileShapingGuidance = () => {
    if (isManualAddGuidanceActive) {
      resetInterimState("profile_shaping_guidance");
    }
  };

  const openSourceShaping = () => {
    clearProfileShapingGuidance();
    situationHandlers.handleSituationExtractThoughtsClick(
      prototype,
      situationId,
    );
  };

  const openManualAdd = () => {
    clearProfileShapingGuidance();
    situationHandlers.handleSituationAddThoughtsClick(prototype, situationId);
  };

  const openSuggestionReview = () => {
    clearProfileShapingGuidance();
    setActivePrototypeCategory(prototype);
    setInterimState("suggested_thoughts_review", {
      isOpen: true,
      value: {
        prototype,
        situation,
      },
    });
  };

  const openEditSituation = () => {
    clearProfileShapingGuidance();
    setInterimState("edit_situation", {
      isOpen: true,
      passedObject: situation,
    });
  };

  const openExportSettings = () => {
    clearProfileShapingGuidance();
    setInterimState("batch_situation_config", {
      isOpen: true,
      value: {
        prototype,
        situationIds: [situationId],
      },
    });
  };

  const openBiasDetector = () => {
    clearProfileShapingGuidance();
    situationHandlers.handleSituationDetectBiasClick(prototype, situationId);
  };

  const startFromSourceAction: ContentItemAction = {
    key: "start_from_source",
    label: PROFILE_SHAPING_LABELS.extractionPhase,
    humanReadable: "Start from source material for this situation.",
    icon: "extract",
    onClick: openSourceShaping,
    color: "moveon.primary",
  };

  const addManuallyAction: ContentItemAction = {
    key: "add_manually",
    label: PROFILE_SHAPING_LABELS.manualPhase,
    humanReadable: "Add confirmed thoughts manually to this situation.",
    icon: "add",
    onClick: openManualAdd,
    color: "moveon.primary",
  };

  const reviewSuggestionsAction: ContentItemAction = {
    key: "review_suggestions",
    label: PROFILE_SHAPING_LABELS.reviewPhase,
    humanReadable: "Review suggested thoughts for this situation.",
    icon: "suggested_thought_review",
    onClick: openSuggestionReview,
    color: "moveon.primary",
  };

  const shapeActions = {
    primaryAction: {
      ...startFromSourceAction,
      key: "shape_situation",
      color: "moveon.primary",
    },
    menuActions: [
      startFromSourceAction,
      addManuallyAction,
      reviewSuggestionsAction,
    ],
    swipeAction: startFromSourceAction,
  } satisfies {
    primaryAction: ContentItemAction;
    menuActions: ContentItemAction[];
    swipeAction: ContentItemAction;
  };

  const lockedPrimaryAction = isLocked
    ? {
        icon: "insight_bias" as GenericIconType,
        onClick: openBiasDetector,
        humanReadable: "Detect bias in related thoughts",
        actionDescription: "Detect bias",
      }
    : null;
  const swipeAction = lockedPrimaryAction ?? {
    onClick: shapeActions.swipeAction.onClick,
    icon: shapeActions.primaryAction.icon,
    humanReadable: shapeActions.primaryAction.humanReadable,
    actionDescription: "Shape",
  };

  const isSituationSelected = isSelectedSituation(prototype, situationId);
  const indexId = `confirmed-${prototype}-${situationId}`;
  const visibleThoughtIds = getIndexHits(indexId);
  const selectedThoughts = getSelectedThoughts(prototype, situationId);
  const selectedThoughtIds =
    selectedThoughts.length > 0 ? selectedThoughts[0].thoughts_ids : [];
  const hasSelectedVisibleChildren =
    visibleThoughtIds.length > 0 &&
    visibleThoughtIds.some((id) => selectedThoughtIds.includes(id));
  const isSectionSelected = isSituationSelected || hasSelectedVisibleChildren;
  const viewTertiaryAction = isLocked
    ? {
        icon: "settings_print" as GenericIconType,
        onClick: openExportSettings,
        humanReadable: "Adjust profile export settings",
        actionDescription: "Export settings",
      }
    : {
        icon: (isSituationSelected ? "unselect" : "select") as GenericIconType,
        onClick: () => {
          situationHandlers.handleSituationOnlySelectionClick(
            prototype,
            situationId,
          );
        },
        humanReadable: isSituationSelected
          ? "Unselect situation only"
          : "Select situation only",
        actionDescription: isSituationSelected
          ? "Unselect situation"
          : "Select situation",
      };
  const situationHeaderActions: ContentItemAction[] = [
    ...(lockedPrimaryAction
      ? [
          {
            key: "bias",
            label: lockedPrimaryAction.actionDescription,
            humanReadable: lockedPrimaryAction.humanReadable,
            icon: lockedPrimaryAction.icon,
            onClick: openBiasDetector,
            color: "moveon.primary",
          },
        ]
      : []),
    {
      key: "edit",
      label: "Edit",
      humanReadable: "Edit situation details",
      icon: "edit",
      onClick: openEditSituation,
    },
    {
      key: isLocked ? "adjust" : "tertiary",
      label: viewTertiaryAction.actionDescription,
      humanReadable: viewTertiaryAction.humanReadable,
      icon: viewTertiaryAction.icon,
      onClick: viewTertiaryAction.onClick,
    },
    {
      key: "delete",
      label: "Delete",
      humanReadable: "Delete situation permanently",
      icon: "delete_permanent",
      onClick: async () => {
        await situationHandlers.handleSituationsDeleteClick(
          {
            situation_ids: [situationId],
            delete_mode: "permanent",
            prototype,
            related_thoughts_action: "remove_anchor",
          },
        );
      },
      tone: "danger",
    },
  ];
  const situationShapeMenuActions = shapeActions.menuActions;

  return (
    <Box>
      <WithSwipeSelector
        key={`swipe-confirmed-${prototype}-${situationId}-${isSituationSelected}-${isSectionSelected}-${countSelectedThoughts()}`}
        isMobile={isMobileDevice}
        selection={{
          isSelected: isSectionSelected,
          checkboxIsSelected: isSituationSelected,
          onToggle: () => {
            const latestVisibleThoughtIds = getIndexHits(indexId);
            situationHandlers.handleSituationSectionSelectionClick({
              prototype,
              situationId,
              activeStudioContent: "confirmed",
              visibleThoughtIds: latestVisibleThoughtIds,
              isSectionSelected,
            });
          },
          humanReadable: isSectionSelected
            ? "Unselect section"
            : "Select section",
          actionDescription: isSectionSelected
            ? "Unselect section"
            : "Select section",
        }}
        action={
          swipeAction && {
            onSwipe: swipeAction.onClick,
            icon: swipeAction.icon,
            humanReadable: swipeAction.humanReadable,
            actionDescription: swipeAction.actionDescription,
          }
        }
        visual={{
          uncoveredBackgroundColor: "moveon.surface.bg",
        }}
      >
        <SituationHeaderItem
          situation={situation}
          isSelected={isSituationSelected}
          actions={situationHeaderActions}
          primaryAction={isLocked ? undefined : shapeActions.primaryAction}
          primaryMenuActions={
            isLocked ? undefined : situationShapeMenuActions
          }
          isPrimaryActionHighlighted={isManualAddGuidanceActive}
          mobileActions={situationHeaderActions}
          onOpenDetail={
            isLocked ? openBiasDetector : shapeActions.primaryAction.onClick
          }
        />
      </WithSwipeSelector>

      <Box py="2" ps={{ base: "0", md: "3" }}>
        {situationContentComponent({
          situationId,
          prototype,
        })}
      </Box>
    </Box>
  );
}
